(() => {
  const root = window.Shopify?.routes?.root || '/';
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionScale = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bf-motion')) || 0;
  const motionEnabled = !motionPreference.matches && motionScale > 0;
  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));
  document.documentElement.classList.toggle('bf-motion-enabled', motionEnabled);
  document.documentElement.classList.toggle('bf-motion-disabled', !motionEnabled);
  const liveRegion = () => document.querySelector('.bf-live-region');
  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 30);
  };
  const focusable = (container) => Array.from(container.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter((element) => !element.hidden && element.offsetParent !== null);
  let previousFocus = null;
  const openDrawer = (id) => {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    previousFocus = document.activeElement;
    if (id === 'CartDrawer') window.BeautyFashionCartUI.setDrawerOpen(true, { restoreFocus: false });
    else {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
    }
    const items = focusable(drawer);
    if (items.length) items[0].focus();
  };
  const closeDrawer = (drawer) => {
    const target = typeof drawer === 'string' ? document.getElementById(drawer) : drawer;
    if (!target) return;
    if (target.id === 'CartDrawer') window.BeautyFashionCartUI.setDrawerOpen(false, { restoreFocus: false });
    else {
      target.classList.remove('is-open');
      target.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('.bf-drawer.is-open,.search-overlay.is-open,dialog[open]')) document.body.classList.remove('is-locked');
    }
    if (previousFocus instanceof HTMLElement) previousFocus.focus();
  };
  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const items = focusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  let motionObserver = null;
  let scrollFrame = 0;
  let lastScrollPosition = window.scrollY;
  const motionGroups = [
    ['.hero-banner__frame', 'hero', 0],
    ['.hero-banner__title,.hero-banner__text,.hero-banner__content>.bf-button', 'fade-up', 110],
    ['.tab-collections__tab', 'fade-up', 70],
    ['.product-card', 'fade-up', 85],
    ['.tab-collections__cta', 'fade-up', 0],
    ['.collection-spotlight__title,.collection-spotlight__intro', 'fade-up', 90],
    ['.collection-spotlight__button', 'fade-up', 65],
    ['.collection-spotlight__media', 'fade-left', 0],
    ['.icons-row__item', 'fade-up', 85],
    ['.overlap-editorial__media', 'fade-up', 0],
    ['.overlap-editorial__content>*', 'fade-up', 100],
    ['.testimonials .bf-section__header,.testimonial-card', 'fade-up', 90],
    ['.lookbook__title', 'fade-up', 0],
    ['.lookbook__media', 'scale-in', 0],
    ['.lookbook__hotspot', 'scale-in', 90],
    ['.sticky-cards__intro>*', 'fade-up', 90],
    ['.sticky-card', 'fade-up', 100],
    ['.products-story__nav a', 'fade-up', 70],
    ['.products-story__item', 'fade-up', 0],
    ['.scroll-banner', 'fade-up', 0],
    ['.split-editorial__media', 'fade-right', 0],
    ['.split-editorial__copy>*', 'fade-up', 95],
    ['.social-gallery__heading>*', 'fade-up', 90],
    ['.social-card', 'fade-up', 85],
    ['.category-banner', 'fade-up', 100],
    ['.main-product>*', 'fade-up', 100],
    ['.collection-hero>*,.page-hero>*', 'fade-up', 90],
    ['.site-footer__grid>*', 'fade-up', 85],
    ['.site-footer__bottom', 'fade-up', 0]
  ];
  const elementsWithin = (scope, selector) => {
    const elements = Array.from(scope.querySelectorAll(selector));
    if (scope instanceof Element && scope.matches(selector)) elements.unshift(scope);
    return elements;
  };
  const getMotionObserver = () => {
    if (motionObserver || !motionEnabled) return motionObserver;
    motionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < .08) return;
        requestAnimationFrame(() => entry.target.classList.add('is-motion-visible'));
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: [0, .08, .15, .35] });
    return motionObserver;
  };
  const initializeMotion = (scope = document) => {
    motionGroups.forEach(([selector, type, delayStep]) => {
      elementsWithin(scope, selector).forEach((element, index) => {
        if (!element.dataset.bfAnimate) element.dataset.bfAnimate = type;
        element.style.setProperty('--bf-delay', `${Math.min(index * delayStep, 440)}ms`);
      });
    });
    elementsWithin(scope, '[data-bf-animate]').forEach((element) => {
      if (!motionEnabled) {
        element.classList.add('is-motion-visible');
        return;
      }
      if (element.dataset.bfMotionReady === 'true') return;
      element.dataset.bfMotionReady = 'true';
      getMotionObserver().observe(element);
    });
  };
  const replayMotion = (scope) => {
    if (!scope) return;
    initializeMotion(scope);
    const elements = elementsWithin(scope, '[data-bf-animate]');
    elements.forEach((element) => element.classList.remove('is-motion-visible'));
    requestAnimationFrame(() => requestAnimationFrame(() => elements.forEach((element) => element.classList.add('is-motion-visible'))));
  };
  const parseSection = (html, selector) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(selector);
  };
  const updateCartSections = async (open, itemCount) => {
    const shouldOpen = open ?? window.BeautyFashionCartUI.isDrawerOpen();
    const drawerSection = document.querySelector('#CartDrawerSection')?.closest('.shopify-section');
    const headerSection = document.querySelector('.site-header')?.closest('.shopify-section');
    const drawerSectionId = drawerSection?.id.replace('shopify-section-', '');
    const headerSectionId = headerSection?.id.replace('shopify-section-', '');
    const sectionIds = [drawerSectionId, headerSectionId].filter(Boolean);
    if (!sectionIds.length) return;
    const response = await fetch(`${root}cart?sections=${sectionIds.join(',')}`);
    const sections = await response.json();
    if (drawerSectionId && sections[drawerSectionId]) {
      const next = parseSection(sections[drawerSectionId], '#CartDrawerSection');
      const current = document.querySelector('#CartDrawerSection');
      if (next && current) current.replaceWith(next);
    }
    const count = parseSection(headerSectionId ? sections[headerSectionId] || '' : '', '[data-cart-count]');
    const nextItemCount = Number.isInteger(itemCount) ? itemCount : Number.parseInt(count?.textContent, 10);
    if (!Number.isNaN(nextItemCount)) window.BeautyFashionCartUI.setCount(nextItemCount);
    window.BeautyFashionCartUI.setDrawerOpen(shouldOpen, { restoreFocus: false });
  };
  const addToCart = async (form) => {
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
      announce(`${data.product_title || 'Item'} added to cart`);
      const modal = document.getElementById('QuickBuyModal');
      if (modal?.open) modal.close();
      await updateCartSections(true, data.item_count);
    } catch (error) {
      announce(error.message);
      const errorBox = form.querySelector('[data-product-error]');
      if (errorBox) {
        errorBox.hidden = false;
        errorBox.textContent = error.message;
      }
    } finally {
      if (submit) submit.disabled = false;
    }
  };
  const updateCartLine = async (key, quantity) => {
    const response = await fetch(`${root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Unable to update cart.');
    await updateCartSections(true, data.item_count);
    announce('Cart updated');
  };
  const loadQuickBuy = async (button) => {
    const modal = document.getElementById('QuickBuyModal');
    if (!modal) return;
    const content = modal.querySelector('[data-quick-buy-content]');
    content.innerHTML = '<p>Loading…</p>';
    modal.showModal();
    document.body.classList.add('is-locked');
    const join = button.dataset.productUrl.includes('?') ? '&' : '?';
    try {
      const response = await fetch(`${button.dataset.productUrl}${join}section_id=quick-buy`);
      const html = await response.text();
      const section = parseSection(html, '[data-quick-buy-section]');
      content.innerHTML = section ? section.innerHTML : '<p>Product unavailable.</p>';
      initializeVariantPickers(content);
      const items = focusable(modal);
      if (items.length) items[0].focus();
    } catch {
      content.innerHTML = '<p>Product unavailable.</p>';
    }
  };
  const initializeVariantPickers = (scope = document) => {
    scope.querySelectorAll('[data-variant-picker]').forEach((picker) => {
      if (picker.dataset.ready === 'true') return;
      picker.dataset.ready = 'true';
      const form = picker.closest('[data-product-root]')?.querySelector('form[action*="/cart/add"]') || picker.closest('form');
      const json = picker.querySelector('[data-product-json]');
      if (!form || !json) return;
      const product = JSON.parse(json.textContent);
      const update = () => {
        const selected = Array.from(picker.querySelectorAll('fieldset')).map((field) => field.querySelector('input:checked')?.value);
        const variant = product.variants.find((item) => item.options.every((option, index) => option === selected[index]));
        const productRoot = form.closest('[data-product-section]') || form.closest('[data-product-root]');
        const idInput = form.querySelector('[name="id"]');
        const submit = form.querySelector('[type="submit"]');
        const availability = productRoot?.querySelector('[data-product-availability]');
        if (!variant) {
          if (submit) { submit.disabled = true; submit.textContent = submit.dataset.unavailableText || 'Unavailable'; }
          if (availability) { availability.textContent = 'Unavailable'; availability.dataset.availability = 'unavailable'; }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (submit) { submit.disabled = !variant.available; submit.textContent = variant.available ? submit.dataset.addText || 'Add to cart' : submit.dataset.soldText || 'Sold out'; }
        if (availability) { availability.textContent = variant.available ? 'In stock' : 'Sold out'; availability.dataset.availability = variant.available ? 'in-stock' : 'sold-out'; }
        const price = productRoot?.querySelector('[data-product-price]');
        if (price && variant.price_formatted) price.textContent = variant.price_formatted;
        const gallery = productRoot?.querySelector('[data-product-gallery]');
        if (gallery && variant.featured_media_id) gallery.dispatchEvent(new CustomEvent('product:media:select', { detail: { id: variant.featured_media_id } }));
        if (window.history?.replaceState && form.closest('.main-product')) window.history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      };
      picker.addEventListener('change', update);
    });
  };
  const initializeProductGalleries = (scope = document) => {
    scope.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';
      const activateMedia = (mediaId, moveFocus = false) => {
        const id = String(mediaId);
        gallery.querySelectorAll('[data-product-media]').forEach((media) => {
          const active = media.dataset.productMedia === id;
          media.classList.toggle('is-active', active);
          media.hidden = !active;
          media.setAttribute('aria-hidden', String(!active));
          if (!active) media.querySelector('video')?.pause();
        });
        gallery.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
          const active = thumbnail.dataset.productThumbnail === id;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-current', String(active));
          if (active && moveFocus) thumbnail.focus({ preventScroll: true });
        });
      };
      gallery.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-product-thumbnail]');
        if (thumbnail) activateMedia(thumbnail.dataset.productThumbnail);
      });
      gallery.addEventListener('product:media:select', (event) => activateMedia(event.detail?.id));
    });
  };
  const initializeStickyCart = (scope = document) => {
    scope.querySelectorAll('[data-sticky-atc]').forEach((sticky) => {
      if (sticky.dataset.stickyReady === 'true') return;
      const form = document.getElementById(sticky.dataset.formId);
      const mainButton = form?.querySelector('[type="submit"][name="add"]');
      const stickyButton = sticky.querySelector('[data-sticky-add]');
      if (!form || !mainButton || !stickyButton) return;
      sticky.dataset.stickyReady = 'true';
      const syncButton = () => {
        stickyButton.disabled = mainButton.disabled;
        stickyButton.textContent = mainButton.textContent;
      };
      const price = form.closest('[data-product-section]')?.querySelector('[data-product-price]');
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      if (price && stickyPrice) new MutationObserver(() => { stickyPrice.textContent = price.textContent; }).observe(price, { childList: true, subtree: true, characterData: true });
      new MutationObserver(syncButton).observe(mainButton, { attributes: true, childList: true, subtree: true });
      stickyButton.addEventListener('click', () => form.requestSubmit(mainButton));
      const visibility = new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting;
      }, { threshold: 0 });
      visibility.observe(mainButton);
      syncButton();
    });
  };
  const activateSpotlight = (section, button) => {
    if (!button || button.getAttribute('aria-selected') === 'true') return;
    section.querySelectorAll('[data-spotlight-button]').forEach((item) => item.setAttribute('aria-selected', String(item === button)));
    section.style.setProperty('--bg-color', button.getAttribute('data-bg'));
    let activeCard = null;
    section.querySelectorAll('[data-spotlight-card]').forEach((card) => {
      const active = card.id === button.getAttribute('aria-controls');
      card.classList.toggle('is-active', active);
      if (active) activeCard = card;
    });
    if (activeCard) replayMotion(activeCard);
  };
  const initializeTabs = (scope = document) => {
    scope.querySelectorAll('[data-tabs]').forEach((tabs) => {
      if (tabs.dataset.tabsReady === 'true') return;
      tabs.dataset.tabsReady = 'true';
      tabs.addEventListener('click', (event) => {
        const button = event.target.closest('[role="tab"]');
        if (!button) return;
        tabs.querySelectorAll('[role="tab"]').forEach((tab) => tab.setAttribute('aria-selected', String(tab === button)));
        const section = tabs.closest('.tab-collections');
        let activePanel = null;
        section.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
          const active = panel.id === button.getAttribute('aria-controls');
          panel.classList.toggle('is-active', active);
          if (active) activePanel = panel;
        });
        replayMotion(activePanel);
      });
    });

    /* Carousel arrows */
    const initCarousels = (root) => {
      root.querySelectorAll('[data-carousel]').forEach((carousel) => {
        if (carousel.dataset.carouselReady === 'true') return;
        carousel.dataset.carouselReady = 'true';
        const track = carousel.querySelector('[data-carousel-track]');
        const prev = carousel.querySelector('[data-carousel-prev]');
        const next = carousel.querySelector('[data-carousel-next]');
        if (!track) return;
        const scrollAmount = () => {
          const slide = track.querySelector('.tab-collections__carousel-slide');
          return slide ? slide.offsetWidth + 16 : 300;
        };
        if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
        if (next) next.addEventListener('click', () => track.scrollBy({ left: scrollAmount(), behavior: 'smooth' }));
      });
    };
    initCarousels(scope);
    scope.querySelectorAll('[data-spotlight]').forEach((section) => {
      if (section.dataset.spotlightReady === 'true') return;
      section.dataset.spotlightReady = 'true';
      ['click', 'pointerover', 'focusin'].forEach((eventName) => section.addEventListener(eventName, (event) => {
        const button = event.target.closest('[data-spotlight-button]');
        if (!button) return;
        activateSpotlight(section, button);
      }));
    });
  };
  const initializeReveal = (scope = document) => {
    scope.querySelectorAll('.reveal-text').forEach((section) => {
      if (section.dataset.revealReady === 'true') return;
      const copy = section.querySelector('.reveal-text__copy');
      const words = section.querySelectorAll('[data-reveal-word]');
      if (!copy || !words.length) return;
      section.dataset.revealReady = 'true';
      const accessibleText = copy.textContent.replace(/\s+/g, ' ').trim();
      copy.setAttribute('aria-label', accessibleText);
      words.forEach((word) => {
        const text = word.textContent;
        word.textContent = '';
        word.setAttribute('aria-hidden', 'true');
        Array.from(text).forEach((character) => {
          const span = document.createElement('span');
          span.className = 'reveal-text__char';
          span.textContent = character;
          word.appendChild(span);
        });
      });
      section._bfRevealChars = Array.from(section.querySelectorAll('.reveal-text__char'));
      if (!motionEnabled) {
        section._bfRevealChars.forEach((character) => { character.style.opacity = '1'; });
        section.classList.add('is-complete');
      }
    });
  };
  const updateRevealProgress = (section, viewportHeight) => {
    const characters = section._bfRevealChars;
    if (!motionEnabled || !characters?.length) return;
    const rect = section.getBoundingClientRect();
    if (rect.top > viewportHeight || rect.bottom < 0) return;
    const duration = Math.max(1, rect.height - viewportHeight * .62);
    const progress = clamp((viewportHeight * .78 - rect.top) / duration);
    const visible = Math.floor(progress * characters.length);
    const tail = [.82, .64, .46, .28];
    const baseColor = getComputedStyle(section).getPropertyValue('--text-color').trim() || '#000000';
    // Parse hex to rgb
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    characters.forEach((character, index) => {
      let alpha = .18;
      if (index < visible) alpha = 1;
      else if (index - visible < tail.length) alpha = tail[index - visible];
      character.style.color = `rgba(${r},${g},${b},${alpha})`;
    });
    section.classList.toggle('is-complete', progress > .72);
  };
  const toggleHotspot = (button, force) => {
    const section = button.closest('.lookbook');
    const popover = document.getElementById(button.getAttribute('aria-controls'));
    if (!section || !popover) return;
    const open = typeof force === 'boolean' ? force : popover.hidden;
    section.querySelectorAll('[data-hotspot-popover]').forEach((item) => { item.hidden = item !== popover || !open; });
    section.querySelectorAll('[data-hotspot]').forEach((item) => item.setAttribute('aria-expanded', String(item === button && open)));
  };
  const initializeHotspots = (scope = document) => {
    scope.querySelectorAll('[data-hotspot]').forEach((button) => {
      if (button.dataset.hotspotReady === 'true') return;
      button.dataset.hotspotReady = 'true';
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') toggleHotspot(button, true);
      });
      button.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse' && button.dataset.hotspotPinned !== 'true') toggleHotspot(button, false);
      });
      button.addEventListener('focus', () => toggleHotspot(button, true));
      button.addEventListener('blur', () => {
        if (button.dataset.hotspotPinned !== 'true') toggleHotspot(button, false);
      });
    });
  };
  const initializeDragScroll = (scope = document) => {
    /* ,.category-banners */
    scope.querySelectorAll('.testimonials__viewport,.social-gallery__viewport').forEach((viewport) => {
      if (viewport.dataset.dragReady === 'true') return;
      viewport.dataset.dragReady = 'true';
      let startX = 0;
      let startLeft = 0;
      let dragging = false;
      let moved = false;
      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        startX = event.clientX;
        startLeft = viewport.scrollLeft;
        dragging = true;
        moved = false;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture?.(event.pointerId);
      });
      viewport.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const distance = event.clientX - startX;
        if (Math.abs(distance) > 4) moved = true;
        viewport.scrollLeft = startLeft - distance;
        if (moved) event.preventDefault();
      });
      const stop = () => {
        dragging = false;
        viewport.classList.remove('is-dragging');
      };
      viewport.addEventListener('pointerup', stop);
      viewport.addEventListener('pointercancel', stop);
      viewport.addEventListener('click', (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }, true);
      viewport.addEventListener('dragstart', (event) => event.preventDefault());
    });
  };
  const initializeTickers = (scope = document) => {
    scope.querySelectorAll('.announcement-bar,.scroll-banner').forEach((ticker) => {
      if (ticker.dataset.tickerReady === 'true') return;
      ticker.dataset.tickerReady = 'true';
      let pauseTimer = 0;
      ticker.addEventListener('mouseenter', () => {
        pauseTimer = window.setTimeout(() => ticker.classList.add('is-ticker-paused'), 500);
      });
      ticker.addEventListener('mouseleave', () => {
        window.clearTimeout(pauseTimer);
        ticker.classList.remove('is-ticker-paused');
      });
    });
  };
  const elementProgress = (element, viewportHeight) => {
    const rect = element.getBoundingClientRect();
    return clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));
  };
  const updateScrollEffects = () => {
    scrollFrame = 0;
    const viewportHeight = window.innerHeight;
    const currentScroll = window.scrollY;
    const header = document.querySelector('.site-header');
    if (header) {
      header.classList.toggle('is-scrolled', currentScroll > 18);
      if (motionEnabled && currentScroll > 160 && currentScroll - lastScrollPosition > 7 && !document.body.classList.contains('is-locked')) header.classList.add('is-compact-hidden');
      if (currentScroll < 120 || lastScrollPosition - currentScroll > 5 || document.body.classList.contains('is-locked')) header.classList.remove('is-compact-hidden');
    }
    lastScrollPosition = currentScroll;
    if (!motionEnabled) return;
    document.querySelectorAll('.hero-banner__frame').forEach((frame) => {
      const progress = elementProgress(frame, viewportHeight);
      frame.style.setProperty('--bf-hero-scale', (1.13 - progress * .1).toFixed(4));
    });
    document.querySelectorAll('.overlap-editorial__media').forEach((media) => {
      const offset = (elementProgress(media, viewportHeight) - .5) * 54;
      media.style.setProperty('--bf-overlap-x-one', `${(-offset * .45).toFixed(2)}px`);
      media.style.setProperty('--bf-overlap-y-one', `${offset.toFixed(2)}px`);
      media.style.setProperty('--bf-overlap-x-two', `${(offset * .45).toFixed(2)}px`);
      media.style.setProperty('--bf-overlap-y-two', `${(-offset).toFixed(2)}px`);
    });
    document.querySelectorAll('.scroll-banner').forEach((banner) => {
      const progress = elementProgress(banner, viewportHeight);
      banner.style.setProperty('--bf-banner-scale', (1.13 - progress * .08).toFixed(4));
      banner.style.setProperty('--bf-banner-y', `${((progress - .5) * 28).toFixed(2)}px`);
    });
    document.querySelectorAll('.split-editorial').forEach((section) => {
      const progress = elementProgress(section, viewportHeight);
      section.style.setProperty('--bf-split-y', `${((.5 - progress) * 46).toFixed(2)}px`);
    });
    const stickyCards = Array.from(document.querySelectorAll('.sticky-card'));
    stickyCards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      card.classList.toggle('is-stuck', rect.top <= 112 && rect.bottom > 180);
      
      let depth = 0;
      const nextCard = stickyCards[index + 1];
      if (nextCard) {
        const nextRect = nextCard.getBoundingClientRect();
        const cardHeight = rect.height || 500;
        const overlapStart = 110 + cardHeight;
        depth = clamp((overlapStart - nextRect.top) / cardHeight, 0, 1);
      }
      
      card.style.setProperty('--bf-card-scale', (1 - depth * .024).toFixed(4));
      card.style.setProperty('--bf-card-image-scale', (1.04 - depth * .025).toFixed(4));
    });
    document.querySelectorAll('.reveal-text').forEach((section) => updateRevealProgress(section, viewportHeight));
  };
  const requestScrollUpdate = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(updateScrollEffects);
  };
  const initializeProductStories = (scope = document) => {
    scope.querySelectorAll('.products-story').forEach((section) => {
      if (section.dataset.storyReady === 'true') return;
      section.dataset.storyReady = 'true';
      
      const tabs = section.querySelectorAll('[data-story-tab]');
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          if (tab.getAttribute('aria-selected') === 'true') return;
          
          tabs.forEach((t) => t.setAttribute('aria-selected', 'false'));
          tab.setAttribute('aria-selected', 'true');
          
          const blockId = tab.getAttribute('aria-controls');
          
          section.querySelectorAll('[data-story-copy]').forEach((copy) => {
            copy.classList.toggle('is-active', copy.id.endsWith(blockId));
          });
          
          section.querySelectorAll('[data-story-media]').forEach((media) => {
            const active = media.id.endsWith(blockId);
            media.classList.toggle('is-active', active);
            if (active) replayMotion(media);
          });
        });
      });
    });
  };
  const initializeDesktopNavigation = (scope = document) => {
    const hoverNavigation = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    elementsWithin(scope, '[data-nav-dropdown]').forEach((dropdown) => {
      if (dropdown.dataset.navReady === 'true') return;
      dropdown.dataset.navReady = 'true';
      const summary = dropdown.querySelector(':scope > summary');
      let closeTimer;
      const closeOtherDropdowns = () => {
        document.querySelectorAll('[data-nav-dropdown][open]').forEach((item) => {
          if (item !== dropdown) item.removeAttribute('open');
        });
      };
      const openDropdown = () => {
        if (!hoverNavigation.matches) return;
        window.clearTimeout(closeTimer);
        closeOtherDropdowns();
        dropdown.setAttribute('open', '');
      };
      const scheduleClose = () => {
        if (!hoverNavigation.matches) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (!dropdown.matches(':hover') && !dropdown.contains(document.activeElement)) dropdown.removeAttribute('open');
        }, 140);
      };
      dropdown.addEventListener('pointerenter', openDropdown);
      dropdown.addEventListener('pointerleave', scheduleClose);
      dropdown.addEventListener('focusin', openDropdown);
      dropdown.addEventListener('focusout', scheduleClose);
      dropdown.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !dropdown.open) return;
        event.preventDefault();
        dropdown.removeAttribute('open');
        summary?.focus();
      });
    });
  };
  const initializeEffects = (scope = document) => {
    initializeMotion(scope);
    initializeTabs(scope);
    initializeReveal(scope);
    initializeHotspots(scope);
    initializeDragScroll(scope);
    initializeTickers(scope);
    initializeProductStories(scope);
    initializeDesktopNavigation(scope);
    initializeProductGalleries(scope);
    initializeStickyCart(scope);
    requestScrollUpdate();
  };
  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('resize', requestScrollUpdate, { passive: true });
  document.addEventListener('beautyfashion:cart-engaged', () => {
    try { sessionStorage.setItem('bf-cart-engaged', 'true'); } catch {}
  });
  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.drawerOpen);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('.bf-drawer') || closer.dataset.drawerClose);
      return;
    }
    const searchOpen = event.target.closest('[data-search-open]');
    if (searchOpen) {
      event.preventDefault();
      const search = document.getElementById('SearchOverlay');
      previousFocus = searchOpen;
      search.classList.add('is-open');
      search.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      search.querySelector('input[type="search"]')?.focus();
      return;
    }
    const searchClose = event.target.closest('[data-search-close]');
    if (searchClose) {
      const search = document.getElementById('SearchOverlay');
      search.classList.remove('is-open');
      search.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
      return;
    }
    const quickBuy = event.target.closest('[data-quick-buy]');
    if (quickBuy) {
      event.preventDefault();
      await loadQuickBuy(quickBuy);
      return;
    }
    const modalClose = event.target.closest('[data-modal-close]');
    if (modalClose) {
      const modal = modalClose.closest('dialog');
      modal.close();
      document.body.classList.remove('is-locked');
      return;
    }
    const hotspot = event.target.closest('[data-hotspot]');
    if (hotspot) {
      const section = hotspot.closest('.lookbook');
      const pinned = hotspot.dataset.hotspotPinned === 'true';
      section.querySelectorAll('[data-hotspot]').forEach((item) => { delete item.dataset.hotspotPinned; });
      if (!pinned) hotspot.dataset.hotspotPinned = 'true';
      toggleHotspot(hotspot, !pinned);
      return;
    }
    const sliderButton = event.target.closest('[data-slider-direction]');
    if (sliderButton) {
      const viewport = document.getElementById(sliderButton.getAttribute('aria-controls'));
      viewport?.scrollBy({ left: viewport.clientWidth * .82 * Number(sliderButton.dataset.sliderDirection), behavior: 'smooth' });
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      try { await updateCartLine(remove.dataset.cartRemove, 0); } catch (error) { announce(error.message); }
    }
  });
  document.addEventListener('change', async (event) => {
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) {
      try { await updateCartLine(quantity.dataset.cartQuantity, Number(quantity.value)); } catch (error) { announce(error.message); }
    }
    if (event.target.matches('[data-localization-select]')) event.target.form?.submit();
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    event.preventDefault();
    await addToCart(form);
  });
  document.addEventListener('keydown', (event) => {
    const openLayer = document.querySelector('.bf-drawer.is-open,.search-overlay.is-open,dialog[open]');
    if (!openLayer) return;
    if (event.key === 'Escape') {
      if (openLayer.matches('dialog')) openLayer.close();
      else if (openLayer.matches('.search-overlay')) {
        openLayer.classList.remove('is-open');
        openLayer.setAttribute('aria-hidden', 'true');
      } else closeDrawer(openLayer);
      document.body.classList.remove('is-locked');
    } else trapFocus(event, openLayer);
  });
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.timer = null;
      this.input?.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(), 180);
      });
    }
    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${root}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[limit]=8`);
        const html = await response.text();
        const section = parseSection(html, '#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
        this.input.setAttribute('aria-expanded', String(Boolean(this.results.textContent.trim())));
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.innerHTML.trim()) return;
      fetch(this.dataset.url).then((response) => response.text()).then((html) => {
        const section = parseSection(html, 'product-recommendations');
        if (section?.innerHTML.trim()) {
          this.innerHTML = section.innerHTML;
          initializeEffects(this);
        }
      }).catch(() => {});
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
  document.querySelectorAll('dialog').forEach((dialog) => dialog.addEventListener('close', () => document.body.classList.remove('is-locked')));
  initializeVariantPickers();
  initializeEffects();
  document.addEventListener('shopify:section:load', (event) => {
    initializeVariantPickers(event.target);
    initializeEffects(event.target);
  });
  window.BeautyFashion = { openDrawer, closeDrawer, updateCartSections };
})();
